import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Select, MenuItem, IconButton, Snackbar } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import client from '../api/client';
import LogoLoader from '../components/Common/LogoLoader';

const API = process.env.REACT_APP_AUTH_API
  ? process.env.REACT_APP_AUTH_API.replace('/api/auth', '/api/ai-agents')
  : 'http://localhost:5000/api/ai-agents';
const TEMPLATES = [
  { id: 'sales', label: '🛒 Sales Agent', prompt: 'You are a professional sales representative. Your job is to understand what the visitor needs, explain how our products/services can help them, handle objections, and guide them towards making a purchase or booking a demo. Be persuasive but not pushy.' },
  { id: 'support', label: '🎧 Support Agent', prompt: 'You are a dedicated customer support agent. Your job is to help resolve customer issues, answer questions about products/services, troubleshoot problems, and ensure the customer feels heard and satisfied. Be empathetic and solution-oriented.' },
  { id: 'lead_qualifier', label: '📋 Lead Qualifier', prompt: 'You are a lead qualification specialist. Your job is to understand the visitor\'s needs by asking about their requirements, budget, timeline, and decision-making process. Qualify them as hot/warm/cold lead and collect their contact details.' },
  { id: 'appointment', label: '📅 Appointment Booker', prompt: 'You are an appointment scheduling assistant. Your job is to help visitors book meetings/consultations. Ask about their preferred date, time, and purpose of the meeting. Collect their name, email, and phone number to confirm.' },
  { id: 'faq', label: '❓ FAQ Bot', prompt: 'You are a FAQ assistant. Your job is to answer common questions about the company, products, services, pricing, and policies based on the website content and training data provided. Be clear and concise.' },
  { id: 'custom', label: '🤖 Custom', prompt: '' },
];

const Section = ({ title, children }) => (
    <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', borderRadius: 3.5,
      border: '1px solid rgba(229,231,235,0.5)', mb: 2.5, position: 'relative', zIndex: 1,
      boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
      transition: 'all 0.2s', '&:hover': { boxShadow: '0 8px 30px rgba(99,102,241,0.06)', borderColor: 'rgba(99,102,241,0.2)' } }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827', mb: 2,
        display: 'flex', alignItems: 'center', gap: 1,
        '&::before': { content: '""', width: 3, height: 16, borderRadius: 2, background: 'linear-gradient(180deg, #6366f1, #8b5cf6)' } }}>
        {title}
      </Typography>
      {children}
    </Box>
  );

export default function AIAgentEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(false);
  const [agent, setAgent] = useState({
    name: '', template: 'custom', status: 'active',
    config: { websiteUrl: '', goal: 'answer_faqs', tone: 'friendly', language: 'en', greeting: 'Hi! How can I help you today?', fallbackMessage: 'Let me connect you with a human agent.', clientBrand: '' },
    training: [], systemPrompt: '',
  });

  useEffect(() => {
    client.get(`${API}/${id}`).then(r => setAgent(r.data)).finally(() => setLoading(false));
  }, [id]);

  const update = (field, value) => setAgent(prev => ({ ...prev, [field]: value }));
  const updateConfig = (field, value) => setAgent(prev => ({ ...prev, config: { ...prev.config, [field]: value } }));

  const handleTemplateChange = (templateId) => {
    const t = TEMPLATES.find(x => x.id === templateId);
    update('template', templateId);
    if (t?.prompt) update('systemPrompt', t.prompt);
  };

  const addTraining = () => setAgent(prev => ({ ...prev, training: [...prev.training, { question: '', answer: '' }] }));
  const updateTraining = (i, field, val) => {
    const t = [...agent.training]; t[i] = { ...t[i], [field]: val }; update('training', t);
  };
  const removeTraining = (i) => update('training', agent.training.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.put(`${API}/${id}`, agent);
      setSnack(true);
    } finally { setSaving(false); }
  };

  if (loading) return <LogoLoader message="Loading agent..." />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', position: 'relative',
      '&::before': { content: '""', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'radial-gradient(600px circle at 30% 20%, rgba(99,102,241,0.06) 0%, transparent 70%), radial-gradient(400px circle at 70% 80%, rgba(139,92,246,0.05) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, position: 'relative', zIndex: 1 }}>
        <IconButton onClick={() => navigate('/app/ai-agents')} sx={{ bgcolor: 'rgba(99,102,241,0.08)', '&:hover': { bgcolor: 'rgba(99,102,241,0.15)' } }}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 20, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {agent.name || 'Edit Agent'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>Configure your AI agent</Typography>
        </Box>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, px: 3,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            boxShadow: '0 4px 15px rgba(99,102,241,0.3)',
            '&:hover': { boxShadow: '0 6px 20px rgba(99,102,241,0.4)' } }}>
          {saving ? 'Saving...' : '💾 Save'}
        </Button>
      </Box>

      {/* Basic */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left Column - Configuration */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
      <Section title="Basic Info">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Agent Name</Typography>
            <TextField fullWidth size="small" value={agent.name} onChange={e => update('name', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Template</Typography>
            <Select fullWidth size="small" value={agent.template} onChange={e => handleTemplateChange(e.target.value)} sx={{ borderRadius: 2 }}>
              {TEMPLATES.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
            </Select>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Website URL</Typography>
            <TextField fullWidth size="small" value={agent.config.websiteUrl} onChange={e => updateConfig('websiteUrl', e.target.value)}
              placeholder="https://yourcompany.com" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
            <Typography sx={{ fontSize: 10, color: '#9ca3af', mt: 0.3 }}>AI will read this website to answer questions</Typography>
          </Box>
        </Box>
      </Section>

      {/* Personality */}
      <Section title="Personality & Appearance">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Goal</Typography>
              <Select fullWidth size="small" value={agent.config.goal} onChange={e => updateConfig('goal', e.target.value)} sx={{ borderRadius: 2 }}>
                <MenuItem value="answer_faqs">Answer FAQs</MenuItem>
                <MenuItem value="collect_email">Collect Email</MenuItem>
                <MenuItem value="qualify_lead">Qualify Lead</MenuItem>
                <MenuItem value="book_appointment">Book Appointment</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Tone</Typography>
              <Select fullWidth size="small" value={agent.config.tone} onChange={e => updateConfig('tone', e.target.value)} sx={{ borderRadius: 2 }}>
                <MenuItem value="friendly">😊 Friendly</MenuItem>
                <MenuItem value="professional">👔 Professional</MenuItem>
                <MenuItem value="casual">😎 Casual</MenuItem>
              </Select>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Client Brand Name</Typography>
            <Typography sx={{ fontSize: 10, color: '#9ca3af', mb: 1 }}>Shown in scheduling confirmation emails (e.g. "Meeting Confirmed — YourBrand")</Typography>
            <TextField fullWidth size="small" placeholder="Talktokam" value={agent.config.clientBrand || ''}
              onChange={e => updateConfig('clientBrand', e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Theme Color</Typography>
              <TextField fullWidth size="small" type="color" value={agent.config.themeColor || '#6366f1'} onChange={e => updateConfig('themeColor', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: 38 } }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Avatar URL</Typography>
              <TextField fullWidth size="small" value={agent.config.avatar || ''} onChange={e => updateConfig('avatar', e.target.value)}
                placeholder="https://..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Greeting Message</Typography>
            <TextField fullWidth size="small" value={agent.config.greeting} onChange={e => updateConfig('greeting', e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>Suggested Questions (one per line)</Typography>
            <TextField fullWidth size="small" multiline rows={3} value={(agent.config.suggestedQuestions || []).join('\n')}
              onChange={e => updateConfig('suggestedQuestions', e.target.value.split('\n').filter(Boolean))}
              placeholder={"What services do you offer?\nHow much does it cost?\nCan I book a demo?"}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.8, color: '#374151' }}>System Prompt</Typography>
            <TextField fullWidth multiline minRows={3} maxRows={5} size="small" value={agent.systemPrompt} onChange={e => update('systemPrompt', e.target.value)}
              placeholder="Describe how the agent should behave..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
          </Box>
        </Box>
      </Section>

      {/* Training */}
      <Section title="Training (Q&A)">
        {agent.training.map((t, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <TextField fullWidth size="small" placeholder="Question" value={t.question} onChange={e => updateTraining(i, 'question', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 12 } }} />
              <TextField fullWidth size="small" placeholder="Answer" value={t.answer} onChange={e => updateTraining(i, 'answer', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 12 } }} />
            </Box>
            <IconButton size="small" onClick={() => removeTraining(i)} sx={{ color: '#ef4444', mt: 0.5 }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addTraining}
          sx={{ textTransform: 'none', color: '#6366f1', fontWeight: 600 }}>Add Q&A</Button>
      </Section>

      {/* Test Chat */}
        </Box>
        {/* Right Column - Actions */}
        <Box sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0 }}>
      <TestChat agentId={id} />

      {/* Embed Code */}
      <Section title="🔗 Embed / Share">
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151', mb: 0.5 }}>Chat Link (share directly):</Typography>
        <TextField fullWidth size="small" value={`${window.location.origin}/agent-chat/${id}`} InputProps={{ readOnly: true }}
          onClick={e => { e.target.select(); navigator.clipboard.writeText(e.target.value); setSnack(true); }}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f9fafb', fontFamily: 'monospace', fontSize: 12 } }} />
        
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151', mb: 0.5 }}>Embed on Website (paste before &lt;/body&gt;):</Typography>
        <TextField fullWidth size="small" multiline rows={3} InputProps={{ readOnly: true }}
          value={`<script>\n(function(){var d=document,s=d.createElement('script');s.src='${window.location.origin}/agent-widget.js';s.setAttribute('data-agent-id','${id}');d.body.appendChild(s);})()\n</script>`}
          onClick={e => { e.target.select(); navigator.clipboard.writeText(e.target.value); setSnack(true); }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f9fafb', fontFamily: 'monospace', fontSize: 11 } }} />
        <Typography sx={{ fontSize: 10, color: '#9ca3af', mt: 0.5 }}>Click to copy. Adds a floating chat button on your website.</Typography>
      </Section>

      {/* Leads */}
      <LeadsSection agentId={id} />
        </Box>
      </Box>

      <Snackbar open={snack} autoHideDuration={3000} onClose={() => setSnack(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{ sx: { borderRadius: 2, fontWeight: 600, bgcolor: '#059669' } }} message="✅ Agent saved!" />
    </Box>
  );
}

function LeadsSection({ agentId }) {
  const [leads, setLeads] = useState([]);
  useEffect(() => {
    client.get(`${API}/${agentId}/leads`).then(r => setLeads(r.data)).catch(() => {});
  }, [agentId]);

  if (leads.length === 0) return null;

  return (
    <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)', borderRadius: 3, border: '1px solid #e2e8f0', mb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827', mb: 1.5 }}>🎯 Captured Leads ({leads.length})</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {leads.slice(0, 20).map((l, i) => (
          <Box key={i} sx={{ p: 1.5, bgcolor: l.tag === 'human_requested' ? '#fef3c7' : '#f9fafb', borderRadius: 2, border: l.tag === 'human_requested' ? '1px solid #fbbf24' : '1px solid #f0f0f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: l.context ? 0.5 : 0 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {l.tag === 'human_requested' && <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#b45309', bgcolor: '#fef3c7', px: 0.8, py: 0.2, borderRadius: 1, border: '1px solid #fbbf24' }}>🙋 Human Requested</Typography>}
                {l.email && <Typography sx={{ fontSize: 12, color: '#374151' }}>📧 {l.email}</Typography>}
                {l.phone && <Typography sx={{ fontSize: 12, color: '#374151' }}>📱 {l.phone}</Typography>}
              </Box>
              <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>{new Date(l.capturedAt).toLocaleDateString()}</Typography>
            </Box>
            {l.context && (
              <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 0.5, whiteSpace: 'pre-line', bgcolor: '#fff', p: 1, borderRadius: 1, border: '1px solid #e5e7eb' }}>
                {l.context}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TestChat({ agentId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const res = await client.post(`${API}/${agentId}/chat`, { message: msg, sessionId: 'test' });
      setMessages(prev => [...prev, { role: 'bot', text: res.data.reply || 'No response' }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'bot', text: err?.response?.data?.message || 'Error' }]);
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)', borderRadius: 3, border: '1px solid #e2e8f0', mb: 2 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827', mb: 1.5 }}>💬 Test Chat</Typography>
      <Box sx={{ minHeight: 200, maxHeight: 300, overflowY: 'auto', mb: 1.5, p: 1.5, bgcolor: '#f9fafb', borderRadius: 2, border: '1px solid #f0f0f0' }}>
        {messages.length === 0 && <Typography sx={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', py: 4 }}>Send a message to test your agent</Typography>}
        {messages.map((m, i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', mb: 1 }}>
            <Box sx={{ px: 1.5, py: 1, borderRadius: 2, maxWidth: '80%', fontSize: 13,
              bgcolor: m.role === 'user' ? '#6366f1' : '#fff',
              color: m.role === 'user' ? '#fff' : '#374151',
              border: m.role === 'bot' ? '1px solid #e5e7eb' : 'none' }}>
              {m.text}
            </Box>
          </Box>
        ))}
        {loading && <Box sx={{ display: 'flex', gap: 0.5, p: 1 }}>{[0,1,2].map(i => <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366f1', animation: `pulse 1s infinite ${i*0.2}s`, '@keyframes pulse': { '0%,100%': { opacity: 0.3 }, '50%': { opacity: 1 } } }} />)}</Box>}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField fullWidth size="small" value={input} onChange={e => setInput(e.target.value)}
          placeholder="Type a message..." onKeyDown={e => e.key === 'Enter' && send()}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }} />
        <Button variant="contained" onClick={send} disabled={!input.trim() || loading}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, minWidth: 70, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
          Send
        </Button>
      </Box>
    </Box>
  );
}
